import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';

import { useAuth } from './auth/AuthProvider';
import RequireRole from './auth/RequireRole';
import { roleHome } from './auth/roleHome';

import LoginPage from './LoginPage';
import LandingPage from './LandingPage';
import TechnicianDashboard from './TechnicianDashboard';
import OperatorDashboard from './OperatorDashboard';
import CustomerDashboard from './CustomerDashboard';
import MaintenanceForm from './pages/MaintenanceForm';
import CustomerSelection from './CustomerSelection';
import OperatorChecklist from './OperatorChecklist';
import CustomerGate from './CustomerGate';
import RepairForm from './RepairForm';

import SignupPage from './SignupPage';

import InvitesPage from './InvitesPage';
import InviteAcceptPage from './InviteAcceptPage';

import ForkliftsPage from './pages/ForkliftsPage';
import TechnicianForkliftsPage from './pages/TechnicianForkliftsPage';
import MaintenancePage from './pages/MaintenancePage';
import UsersPage from './pages/UsersPage';
import BillingPage from './pages/BillingPage';
import CustomerUsersRoute from './auth/CustomerUsersRoute';
import CompanyDetailsPage from './pages/CompanyDetailsPage';
import ReportsPage from './pages/ReportsPage';
import SettingsPage from './pages/SettingsPage';
import TermsPage from './legal/TermsPage';
import PrivacyPage from './legal/PrivacyPage';
import SubprocessorsPage from './legal/SubprocessorsPage';


function LegacyCustomerRedirect() {
  const location = useLocation();
  return (
    <Navigate
      to={{ pathname: '/fleet_dashboard', search: location.search }}
      replace
    />
  );
}

export function HomeRedirect() {
  const { session } = useAuth();
  const role = String(session?.role || '').toLowerCase();
  if (!role) return <LandingPage />;
  return <Navigate to={roleHome(role)} replace />;
}

function RoleRoute({ allow = [], children }) {
  const { session } = useAuth();
  const role = String(session?.role || '').toLowerCase();
  if (!role) return <Navigate to="/" replace />;
  if (!allow.includes(role)) return <Navigate to={roleHome(role)} replace />;
  return children;
}

export function CustomerOwnerRoute({ children }) {
  const { session } = useAuth();
  if (String(session?.role || '').toLowerCase() !== 'customer' || session?.customerAccountRole !== 'OWNER') {
    return <Navigate to={roleHome(String(session?.role || '').toLowerCase())} replace />;
  }
  return children;
}

export function CustomerSettingsRoute({ children }) {
  const { session } = useAuth();
  const role = String(session?.role || '').toLowerCase();
  const accountRole = String(session?.customerAccountRole || '').toUpperCase();
  if (role !== 'customer' || !['OWNER', 'MANAGER'].includes(accountRole)) {
    return <Navigate to={roleHome(role)} replace />;
  }
  return children;
}

export function AppRoutes() {
  const { ready } = useAuth();
  if (!ready) return null;

  return (
    <Routes>
      <Route path="/" element={<HomeRedirect />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/subprocessors" element={<SubprocessorsPage />} />

      <Route path="/signup" element={<SignupPage />} />
      <Route path="/signup/technician" element={<Navigate to="/" replace />} />
      <Route path="/signup/operator" element={<Navigate to="/" replace />} />

      <Route path="/invite" element={<InviteAcceptPage />} />
      <Route path="/invite/technician" element={<InviteAcceptPage />} />
      <Route path="/invite/operator" element={<InviteAcceptPage />} />

      <Route
        path="/customer/company"
        element={
          <CustomerOwnerRoute>
            <CompanyDetailsPage />
          </CustomerOwnerRoute>
        }
      />
      <Route
        path="/customer/settings"
        element={<CustomerSettingsRoute><SettingsPage /></CustomerSettingsRoute>}
      />
      <Route
        path="/invites"
        element={
          <RoleRoute allow={['technician', 'operator', 'customer']}>
            <InvitesPage />
          </RoleRoute>
        }
      />

      <Route
        path="/select-customer"
        element={
          <RoleRoute allow={['technician', 'operator']}>
            <CustomerSelection />
          </RoleRoute>
        }
      />

      <Route
        path="/technician-dashboard"
        element={
          <RequireRole role="technician">
            <CustomerGate role="technician">
              <TechnicianDashboard />
            </CustomerGate>
          </RequireRole>
        }
      />
      <Route path="/technician" element={<Navigate to="/technician-dashboard" replace />} />

      <Route
        path="/dashboard"
        element={
          <RequireRole role="technician">
            <CustomerGate role="technician">
              <TechnicianDashboard />
            </CustomerGate>
          </RequireRole>
        }
      />

      <Route
        path="/operator"
        element={
          <RequireRole role="operator">
            <CustomerGate role="operator">
              <OperatorDashboard />
            </CustomerGate>
          </RequireRole>
        }
      />

      <Route
        path="/fleet_dashboard"
        element={
          <RequireRole role="customer">
            <CustomerDashboard />
          </RequireRole>
        }
      />
      <Route
        path="/customer"
        element={
          <RequireRole role="customer">
            <LegacyCustomerRedirect />
          </RequireRole>
        }
      />

      <Route
        path="/maintenance/:unitId"
        element={
          <RequireRole role="technician">
            <CustomerGate role="technician">
              <MaintenanceForm />
            </CustomerGate>
          </RequireRole>
        }
      />
      <Route
        path="/maintenance-form/:unitId"
        element={
          <RequireRole role="technician">
            <CustomerGate role="technician">
              <MaintenanceForm />
            </CustomerGate>
          </RequireRole>
        }
      />

      <Route
        path="/repairs/new"
        element={
          <RequireRole role="technician">
            <CustomerGate role="technician">
              <RepairForm />
            </CustomerGate>
          </RequireRole>
        }
      />
      <Route
        path="/repairs/:unitId"
        element={
          <RequireRole role="technician">
            <CustomerGate role="technician">
              <RepairForm />
            </CustomerGate>
          </RequireRole>
        }
      />

      <Route
        path="/operator/checklist"
        element={
          <RequireRole role="operator">
            <CustomerGate role="operator">
              <OperatorChecklist />
            </CustomerGate>
          </RequireRole>
        }
      />
      <Route path="/operator/submissions" element={<Navigate to="/operator" replace />} />

      <Route
        path="/customer/forklifts"
        element={
          <RequireRole role="customer">
            <ForkliftsPage />
          </RequireRole>
        }
      />
      <Route
        path="/technician/forklifts"
        element={
          <RequireRole role="technician">
            <CustomerGate role="technician">
              <TechnicianForkliftsPage />
            </CustomerGate>
          </RequireRole>
        }
      />

      <Route
        path="/customer/maintenance"
        element={
          <RequireRole role="customer">
            <MaintenancePage />
          </RequireRole>
        }
      />
      <Route
        path="/customer/reports"
        element={<RequireRole role="customer"><ReportsPage /></RequireRole>}
      />
      <Route
        path="/customer/billing"
        element={
          <RequireRole role="customer">
            <BillingPage />
          </RequireRole>
        }
      />

      <Route
        path="/customer/users"
        element={
          <CustomerUsersRoute>
            <UsersPage />
          </CustomerUsersRoute>
        }
      />

      <Route
        path="/operator/maintenance"
        element={
          <RequireRole role="operator">
            <CustomerGate role="operator">
              <MaintenancePage />
            </CustomerGate>
          </RequireRole>
        }
      />
      <Route
        path="/technician/maintenance"
        element={
          <RequireRole role="technician">
            <CustomerGate role="technician">
              <MaintenancePage />
            </CustomerGate>
          </RequireRole>
        }
      />

      <Route
        path="/customer/maintenance/view"
        element={
          <RequireRole role="customer">
            <MaintenanceForm />
          </RequireRole>
        }
      />
      <Route
        path="/technician/maintenance/view"
        element={
          <RequireRole role="technician">
            <CustomerGate role="technician">
              <MaintenanceForm />
            </CustomerGate>
          </RequireRole>
        }
      />

      <Route
        path="/customer/repair/view"
        element={
          <RequireRole role="customer">
            <RepairForm />
          </RequireRole>
        }
      />
      <Route
        path="/technician/repair/view"
        element={
          <RequireRole role="technician">
            <CustomerGate role="technician">
              <RepairForm />
            </CustomerGate>
          </RequireRole>
        }
      />

      <Route
        path="/customer/operator-checklist/view"
        element={
          <RequireRole role="customer">
            <OperatorChecklist />
          </RequireRole>
        }
      />
      <Route
        path="/technician/operator-checklist/view"
        element={
          <RequireRole role="technician">
            <CustomerGate role="technician">
              <OperatorChecklist />
            </CustomerGate>
          </RequireRole>
        }
      />

      <Route path="*" element={<HomeRedirect />} />
    </Routes>
  );
}

export default function App() {
  return <AppRoutes />;
}
